import React from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { CheckCircle, ShieldCheck, ArrowRight, Loader2, Download } from 'lucide-react';
import { useAuth } from '../../components/self-service/auth/useAuth';
import { usePublisherConfig } from '../../data/publisherConfigs';
import SelfServiceLayout from '../../components/self-service/SelfServiceLayout';
import CampaignStatusDashboard from '../../components/self-service/CampaignStatusDashboard';
import OrderReceipt from '../../components/self-service/OrderReceipt';
import { apiClient } from '../../lib/apiClient';
import * as analytics from '../../lib/analytics';
import { EVENTS } from '../../lib/analytics';
import { formatMoneySmart } from '../../components/self-service/shared/format';
import { exportReceiptToPdf, receiptFilenameFor } from '../../services/orderReceiptPdf';
import { calculateImpressions, type SelfServiceSubmissionRow } from '../../types/selfService';
import Banner from '../../components/self-service/shared/Banner';

const SubmittedPage: React.FC = () => {
  const { slug, submissionId } = useParams<{ slug: string; submissionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, selectedAdvertiser } = useAuth();
  const { config, loading: configLoading } = usePublisherConfig(slug);

  // Seed from the wizard's navigation state when available so the initial
  // render shows real values without waiting on /list-by-advertiser. Refresh
  // loses location.state — the fetch below covers that case.
  //
  // Seat redesign: the claim-token / magic-link auto-send is gone. The user
  // is always signed in by the time they reach this page (verify-email at
  // step 4 is mandatory) so there's no anonymous path to bridge.
  const navState = location.state as {
    submission?: Partial<SelfServiceSubmissionRow>;
  } | null;
  const navStateSubmission = navState?.submission;
  const [row, setRow] = React.useState<SelfServiceSubmissionRow | null>(
    navStateSubmission ? (navStateSubmission as SelfServiceSubmissionRow) : null,
  );
  const [loading, setLoading] = React.useState(!navStateSubmission);
  const [error, setError] = React.useState<string | null>(null);
  const [downloading, setDownloading] = React.useState(false);
  const [downloadError, setDownloadError] = React.useState<string | null>(null);

  // The page is taller than the review step, so the browser's default
  // scroll-restoration lands users below the fold. Force the page to the
  // top once on mount — matches SelfServiceWizard's behavior on step change.
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  // TODO: Email this receipt as a PDF attachment after submission. Hook into
  // the self-service transactional email service (currently the Resend stub
  // used by selfservice-draft-resume-emailer) so the buyer gets the same PDF
  // in their inbox without having to click Download. Tracking outside of
  // Linear until our issue limit clears.
  const handleDownloadReceipt = async () => {
    if (!row || !config) return;
    setDownloadError(null);
    setDownloading(true);
    try {
      await exportReceiptToPdf({ row, config }, receiptFilenameFor(row.id, row.created_at));
      analytics.track(EVENTS.RECEIPT_DOWNLOADED, { station_slug: config.slug, submission_id: row.id });
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Failed to generate PDF');
    } finally {
      setDownloading(false);
    }
  };

  React.useEffect(() => {
    const advertiserId = selectedAdvertiser?.advertiserId;
    if (!advertiserId || !submissionId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { rows } = await apiClient.post<{ rows: SelfServiceSubmissionRow[] }>(
          '/submissions/list-by-advertiser',
          { advertiserId, stationSlug: slug },
        );
        if (cancelled) return;
        const found = (rows ?? []).find((r) => r.id === submissionId) ?? null;
        // Don't clobber a seeded row with null — the fetch can race a fresh
        // submission's commit, and we'd rather show the optimistic values
        // than flash to "—" until the next focus refetch.
        if (found) setRow(found);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load campaign');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedAdvertiser?.advertiserId, submissionId, slug]);

  // The wizard waits for Stripe's webhook to populate card brand + last4
  // before navigating here, so the row arrives ready. If the webhook
  // misfires the gate below shows a polite "receipt unavailable" fallback
  // instead of an empty card-less receipt.
  const cardDataReady = Boolean(row?.payment_method_brand && row?.payment_method_last4);

  if (configLoading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">Loading…</div>;
  }
  if (!config) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Page Not Found</h1>
          <p className="text-slate-600 text-lg">This portal doesn't exist or is no longer active.</p>
        </div>
      </div>
    );
  }

  // The campaign label preference: explicit campaignName, then businessName,
  // then a fallback so the card never shows blank if data is mid-loading.
  const campaignLabel = row?.campaign?.campaignName?.trim() || row?.business_info?.businessName || '—';
  const budget = row?.campaign?.budget;
  const cpm = config.products.find((p) => p.id === row?.campaign?.selectedProduct)?.cpm ?? 0;
  const adPlays = budget != null && cpm > 0 ? calculateImpressions(budget, cpm) : null;
  const contactEmail = row?.contact_email || user?.email || '';

  return (
    <SelfServiceLayout config={config}>
      <div className="max-w-3xl mx-auto bg-white border border-slate-200 shadow-sm rounded-xl p-6 md:p-10">
        <div className="flex items-center gap-4 mb-8">
          <CheckCircle className="w-14 h-14 text-emerald-500 flex-shrink-0" />
          <div>
            {row?.status === 'approved' ? (
              <>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
                  Your campaign is live
                </h2>
                <p className="text-slate-700 mt-1">
                  Every commercial was previously approved at{' '}
                  <span className="text-slate-900 font-medium">{config.name}</span>, so we approved
                  this campaign automatically. Delivery starts on{' '}
                  <span className="text-slate-900 font-medium">
                    {row.campaign?.startDate
                      ? new Date(`${String(row.campaign.startDate).slice(0, 10)}T00:00:00`).toLocaleDateString(
                          undefined,
                          { month: 'long', day: 'numeric', year: 'numeric' },
                        )
                      : 'your campaign start date'}
                  </span>
                  .
                </p>
              </>
            ) : (
              <>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Campaign submitted</h2>
                <p className="text-slate-700 mt-1">
                  {contactEmail ? (
                    <>
                      We'll review your commercial and notify you at{' '}
                      <span className="text-slate-900 font-medium">{contactEmail}</span> once it's approved and live.
                    </>
                  ) : (
                    <>We'll review your commercial and notify you once it's approved and live.</>
                  )}
                </p>
              </>
            )}
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 mb-4 grid grid-cols-1 sm:grid-cols-3 gap-5 sm:divide-x sm:divide-slate-200">
          <div className="sm:pr-5">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Campaign</div>
            <div className="text-slate-900 font-semibold truncate">
              {loading && !row ? <Loader2 className="w-4 h-4 animate-spin inline" /> : campaignLabel}
            </div>
          </div>
          <div className="sm:px-5">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Budget</div>
            <div className="text-slate-900 font-semibold">
              {budget != null ? `$${formatMoneySmart(budget)}` : (loading ? <Loader2 className="w-4 h-4 animate-spin inline" /> : '—')}
            </div>
          </div>
          <div className="sm:pl-5">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Ad plays</div>
            <div className="text-slate-900 font-semibold tabular-nums">
              {adPlays != null
                ? adPlays.toLocaleString()
                : (loading ? <Loader2 className="w-4 h-4 animate-spin inline" /> : '—')}
            </div>
          </div>
        </div>

        <div className="mb-8">
          <Banner tone="success" label="No charge yet" icon={<ShieldCheck className="w-3 h-3" />}>
            {row?.payment_plan === 'monthly' ? (
              <>
                Your card is saved, but no charge has been made. If your commercial is approved,
                the first installment is charged on your campaign start date and future
                installments charge automatically on each anniversary date.
              </>
            ) : (
              <>
                We placed a hold on your card. You'll only be charged once your commercial is
                approved. If it's rejected, the hold is released and nothing comes off your card.
              </>
            )}
          </Banner>
        </div>

        {error && (
          <div className="mb-6">
            <Banner tone="warn" label="Couldn't fetch details">
              {error}. Your campaign was submitted — check your email for confirmation.
            </Banner>
          </div>
        )}

        <div className="pt-8 border-t border-slate-200">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-5">
            Campaign Status
          </div>
          <CampaignStatusDashboard
            config={config}
            currentStep={row?.status === 'approved' ? 2 : 1}
          />
        </div>

        {row && cardDataReady ? (
          <div className="pt-8 mt-8 border-t border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Order Receipt
              </div>
              <button
                type="button"
                onClick={handleDownloadReceipt}
                disabled={downloading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downloading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                {downloading ? 'Generating…' : 'Download PDF'}
              </button>
            </div>
            {downloadError && (
              <div className="mb-3">
                <Banner tone="warn" label="Couldn't generate PDF">{downloadError}</Banner>
              </div>
            )}
            <OrderReceipt row={row} config={config} />
          </div>
        ) : row ? (
          <div className="pt-8 mt-8 border-t border-slate-200">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-4">
              Order Receipt
            </div>
            <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded p-4">
              Your receipt isn't ready yet. Refresh the page to retry, or view it later from{' '}
              <Link to={`/${slug}/my-campaigns`} className="underline">My campaigns</Link>.
            </div>
          </div>
        ) : null}

        <div className="mt-8 flex items-center justify-between gap-3">
          {submissionId ? (
            <Link
              to={`/${slug}/my-campaigns/${submissionId}`}
              className="text-sm font-medium text-slate-600 hover:text-slate-900"
            >
              View campaign details →
            </Link>
          ) : <span />}
          <button
            type="button"
            onClick={() => navigate(`/${slug}/my-campaigns`)}
            className="group inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full text-[13px] font-semibold tracking-tight text-white transition-all hover:brightness-110 active:scale-[0.98]"
            style={{
              background: `linear-gradient(180deg, ${config.accentColor || config.primaryColor}, ${config.primaryColor})`,
              boxShadow: `0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 16px -8px ${config.primaryColor}`,
            }}
          >
            View my campaigns
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>
    </SelfServiceLayout>
  );
};

export default SubmittedPage;
